/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/

/**
 * offlineDb.js
 *
 * One IndexedDB database per capture assignment token, used to hold
 * captured media blobs and a queue of pending server mutations while
 * the device is offline (or a request fails at the network level).
 *
 * Stores:
 *   - media: captured File/Blob + metadata, keyed by a locally generated id
 *   - mutations: an ordered queue of server operations to replay, keyed by
 *     an auto-incrementing id and indexed by targetKey for supersession
 *   - assets: read-side cache of remote images (branding/reference/preview),
 *     keyed by URL, populated by utils/assetCache.js
 *
 * There is no separate "snapshot" store — capturedFiles/completedItemsMedia
 * are reconstructed on load from a fresh GET plus a query over `media`,
 * since in-memory File objects don't survive a reload anyway.
 */

import { openDB, deleteDB } from 'idb';
import { toUtc } from './dateTime';

const DB_VERSION = 3;
const STALE_DB_MAX_AGE_MS = 48 * 60 * 60 * 1000; // 48 hours

const dbNameForToken = (token) => `capture-offline-${token}`;

const dbPromises = new Map();

const openCaptureDb = (token) => {
  if (!dbPromises.has(token)) {
    const promise = openDB(dbNameForToken(token), DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('media')) {
          const mediaStore = db.createObjectStore('media', { keyPath: 'localId' });
          mediaStore.createIndex('assignmentItemId', 'assignmentItemId');
          mediaStore.createIndex('targetKey', 'targetKey');
        }
        if (!db.objectStoreNames.contains('mutations')) {
          const mutationsStore = db.createObjectStore('mutations', { keyPath: 'id', autoIncrement: true });
          mutationsStore.createIndex('targetKey', 'targetKey');
          mutationsStore.createIndex('status', 'status');
          mutationsStore.createIndex('createdAt', 'createdAt');
        }
        if (!db.objectStoreNames.contains('meta')) {
          db.createObjectStore('meta', { keyPath: 'key' });
        }
        if (!db.objectStoreNames.contains('assets')) {
          // Read-side cache: branding/reference/preview images fetched once
          // per token so the Capture UI still renders them while offline.
          db.createObjectStore('assets', { keyPath: 'url' });
        }
        if (!db.objectStoreNames.contains('notes')) {
          // Local mirror of the notes list: every note the last successful
          // GET returned (syncStatus 'synced'), plus any not-yet-synced
          // local create/edit/delete overlaid on top. keyPath is stable for
          // a note's whole lifetime — a server-sourced note uses its server
          // id as localId; a locally-created one keeps its client-generated
          // localId even after the create syncs (serverId gets filled in).
          db.createObjectStore('notes', { keyPath: 'localId' });
        }
      },
    });
    dbPromises.set(token, promise);
  }
  return dbPromises.get(token);
};

const buildPhotoTargetKey = (assignmentItemId, mediaLocalId) => `${assignmentItemId}:${mediaLocalId}`;

/**
 * Persist a captured blob locally. Returns the generated localId.
 */
const saveMedia = async (token, { assignmentItemId, blob, fileName, mimeType, identifierText = null, customerIdentifierText = null, duration = null, replaceUploadContext = null, assignmentItem = null, provenanceData = null, useMultipart = false, capturedByName = null, capturedByEmail = null }) => {
  const db = await openCaptureDb(token);
  const localId = (window.crypto?.randomUUID?.() ?? `local_${Date.now()}_${Math.random().toString(16).slice(2)}`);
  // Stored as an ArrayBuffer, never the native Blob — confirmed via
  // [blob-diag] logging that WebKit (Safari/iOS, incl. iOS Chrome — every
  // iOS browser runs on WebKit) can silently corrupt a Blob persisted in
  // IndexedDB: the record survives, but reading its data back later throws
  // `NotFoundError: The object can not be found here` (the on-disk backing
  // file WebKit uses for Blobs is gone). The half-readable File this produced
  // downstream is exactly what caused the server's "Request aborted" — the
  // browser sent fewer bytes than the declared Content-Length and the
  // connection was torn down mid-upload. An ArrayBuffer has no external
  // disk-backed storage — IndexedDB's structured clone stores it as inline
  // binary data in the record itself, sidestepping that failure mode
  // entirely. Read sites (buildReplayFile below, Capture.jsx's
  // hydrateCapturedFilesFromQueue) reconstruct a File/Blob from this buffer
  // on demand.
  const blobBuffer = await blob.arrayBuffer();
  const record = {
    localId,
    assignmentItemId,
    targetKey: buildPhotoTargetKey(assignmentItemId, localId),
    blob: blobBuffer,
    fileName,
    mimeType,
    identifierText,
    customerIdentifierText,
    duration,
    replaceUploadContext,
    assignmentItem,
    provenanceData,
    useMultipart,
    capturedByName,
    capturedByEmail,
    status: 'pending',
    serverMediaFileId: null,
    createdAt: Date.now(),
  };
  try {
    await db.put('media', record);
  } catch (error) {
    // Safari/WebKit Private Browsing refuses to persist Blob-backed data into
    // IndexedDB (no on-disk temp storage is allowed there) and throws a
    // generic `UnknownError: Error preparing Blob/File data to be stored in
    // object store` — indistinguishable from any other IndexedDB failure by
    // name alone. Tag it so callers (MediaUploader's parseUploadError) can
    // tell the user the real cause instead of misreporting it as "no
    // internet" just because the device also happens to be offline.
    const isBlobStorageUnsupported =
      error?.name === 'UnknownError' && /prepar(e|ing) Blob\/File data/i.test(error?.message || '');
    if (isBlobStorageUnsupported) {
      const wrapped = new Error(
        "Offline capture isn't supported in Private Browsing mode on this browser. Please reopen this link in a regular (non-private) window and try again."
      );
      wrapped.name = 'BlobStorageUnsupportedError';
      wrapped.cause = error;
      throw wrapped;
    }
    throw error;
  }
  return localId;
};

const getMedia = async (token, localId) => {
  const db = await openCaptureDb(token);
  return db.get('media', localId);
};

const updateMediaStatus = async (token, localId, patch) => {
  const db = await openCaptureDb(token);
  const existing = await db.get('media', localId);
  if (!existing) return;
  await db.put('media', { ...existing, ...patch });
};

/**
 * Media rows still genuinely queued for sync (pending/syncing), oldest
 * first. Deliberately excludes 'failed' — that means a real server
 * rejection with no queued mutation to replay it, so it shouldn't block
 * resume-routing or count as "still syncing".
 */
const getPendingMedia = async (token) => {
  const db = await openCaptureDb(token);
  const all = await db.getAll('media');
  return all
    .filter((m) => ['pending', 'syncing'].includes(m.status))
    .sort((a, b) => a.createdAt - b.createdAt);
};

/** Distinct assignmentItemIds that have any not-yet-synced media queued. */
const getQueuedAssignmentItemIds = async (token) => {
  const pending = await getPendingMedia(token);
  return [...new Set(pending.map((m) => m.assignmentItemId))];
};

const enqueueMutation = async (token, { type, assignmentItemId, targetKey, mediaLocalId = null, payload = null }) => {
  const db = await openCaptureDb(token);
  const record = {
    type,
    assignmentItemId,
    targetKey,
    mediaLocalId,
    payload,
    status: 'pending',
    retryCount: 0,
    lastError: null,
    createdAt: Date.now(),
  };
  const id = await db.add('mutations', record);
  return { ...record, id };
};

/** Pending mutations in creation order (oldest first) — the flush order. */
const getPendingMutations = async (token) => {
  const db = await openCaptureDb(token);
  const all = await db.getAll('mutations');
  return all
    .filter((m) => m.status === 'pending')
    .sort((a, b) => a.createdAt - b.createdAt);
};

const updateMutation = async (token, id, patch) => {
  const db = await openCaptureDb(token);
  const existing = await db.get('mutations', id);
  if (!existing) return;
  await db.put('mutations', { ...existing, ...patch });
};

/**
 * Mark all still-pending mutations sharing a targetKey as superseded,
 * except the one just created (keepId). Used so retake/delete/skip never
 * race a now-stale queued operation for the same item/photo slot.
 */
const supersedeMutations = async (token, targetKey, keepId) => {
  const db = await openCaptureDb(token);
  const tx = db.transaction('mutations', 'readwrite');
  const index = tx.store.index('targetKey');
  let cursor = await index.openCursor(IDBKeyRange.only(targetKey));
  while (cursor) {
    const record = cursor.value;
    if (record.status === 'pending' && record.id !== keepId) {
      await cursor.update({ ...record, status: 'superseded' });
    }
    cursor = await cursor.continue();
  }
  await tx.done;
};

/**
 * Item-level supersession: a new deleteItem/skip mutation for an
 * assignmentItemId cancels every pending upload/skip/beginCapture
 * mutation under that item (all photo slots included).
 */
const supersedeMutationsForItem = async (token, assignmentItemId, keepId) => {
  const db = await openCaptureDb(token);
  const tx = db.transaction('mutations', 'readwrite');
  let cursor = await tx.store.openCursor();
  while (cursor) {
    const record = cursor.value;
    if (record.assignmentItemId === assignmentItemId && record.status === 'pending' && record.id !== keepId) {
      await cursor.update({ ...record, status: 'superseded' });
    }
    cursor = await cursor.continue();
  }
  await tx.done;
};

/**
 * Cancel a queued-but-unsynced media item (retake/delete before it ever
 * synced). Soft-cancel so an in-flight replay can still notice and back out.
 */
const cancelQueuedUpload = async (token, localId) => {
  if (!localId) return;
  await updateMediaStatus(token, localId, { status: 'cancelled' });
  const db = await openCaptureDb(token);
  const tx = db.transaction('mutations', 'readwrite');
  let cursor = await tx.store.openCursor();
  while (cursor) {
    const record = cursor.value;
    if (record.mediaLocalId === localId && record.status === 'pending') {
      await cursor.update({ ...record, status: 'cancelled' });
    }
    cursor = await cursor.continue();
  }
  await tx.done;
};

// ── Notes (local mirror + optimistic offline create/edit/delete) ────────────

const upsertNoteLocal = async (token, record) => {
  const db = await openCaptureDb(token);
  await db.put('notes', record);
};

/** Non-deleted local notes, oldest first (matches the server's append order). */
const getNotesLocal = async (token) => {
  const db = await openCaptureDb(token);
  const all = await db.getAll('notes');
  return all
    .filter((n) => !n.deletedAt)
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
};

const getNoteLocal = async (token, localId) => {
  const db = await openCaptureDb(token);
  return db.get('notes', localId);
};

/** Hard-remove a note record — only for one that never synced (nothing server-side to reflect the delete). */
const removeNoteLocal = async (token, localId) => {
  const db = await openCaptureDb(token);
  await db.delete('notes', localId);
};

/**
 * Replace the local notes mirror with a fresh server GET, then re-apply any
 * still-pending edit/delete on top (a queued mutation that hasn't replayed
 * yet must keep winning over the server's last-known state). A pending
 * *create* is never touched here — it's local-only until its own addNote
 * mutation syncs, so it isn't present in serverNotes at all yet.
 */
const reconcileNotesFromServer = async (token, serverNotes) => {
  const db = await openCaptureDb(token);
  const pendingMutations = await getPendingMutations(token);
  const NOTE_MUTATION_TYPES = ['updateNote', 'deleteNote'];
  const pendingByTargetKey = new Map(
    pendingMutations.filter((m) => NOTE_MUTATION_TYPES.includes(m.type)).map((m) => [m.targetKey, m])
  );

  const tx = db.transaction('notes', 'readwrite');
  const existingNotes = await tx.store.getAll();
  // A note created offline keeps its client-generated localId for its whole
  // lifetime (see the notes store docstring above) — serverId is what ties
  // it back to serverNote.id, so resolve the real localId through that
  // instead of assuming localId === serverNote.id (a note we've never seen
  // locally before, e.g. from another participant, has no such row and
  // falls back to using the server id as its localId, same as before).
  const localIdByServerId = new Map(
    existingNotes.filter((n) => n.serverId != null).map((n) => [n.serverId, n.localId])
  );
  const existingSyncedIds = new Set(
    existingNotes.filter((n) => n.syncStatus === 'synced').map((n) => n.localId)
  );
  const serverLocalIds = new Set(
    serverNotes.map((n) => localIdByServerId.get(n.id) ?? n.id)
  );

  await Promise.all([
    ...serverNotes.map((serverNote) => {
      const localId = localIdByServerId.get(serverNote.id) ?? serverNote.id;
      const pending = pendingByTargetKey.get(localId);
      // A pending edit/delete for this note overrides what the server just returned.
      if (pending?.type === 'deleteNote') {
        return tx.store.put({ ...serverNote, localId, serverId: serverNote.id, syncStatus: 'pending', deletedAt: toUtc(new Date()) });
      }
      if (pending?.type === 'updateNote') {
        return tx.store.put({ ...serverNote, ...pending.payload, localId, serverId: serverNote.id, syncStatus: 'pending', deletedAt: null });
      }
      return tx.store.put({ ...serverNote, localId, serverId: serverNote.id, syncStatus: 'synced', deletedAt: null });
    }),
    // Drop previously-synced local rows the server no longer lists (e.g.
    // deleted by another participant, or our own delete already landed).
    ...[...existingSyncedIds].filter((id) => !serverLocalIds.has(id)).map((id) => tx.store.delete(id)),
  ]);

  await tx.done;
};

/**
 * Persist the current session token (uid) so it survives past the browser
 * cookie's own lifetime. The cookie set by cookieService.setSessionToken is
 * deliberately capped to expire at the assignment's absolute deadline (see
 * its docstring), so a device that goes offline and reconnects after that
 * deadline has already lost the cookie by the time it needs to replay its
 * queued mutations — exactly when the server's offline-replay bypass needs
 * proof of who it's talking to. This mirrors the cookie's value into the
 * same storage the offline queue itself already depends on, which isn't
 * subject to that wall-clock eviction.
 */
const setSessionCredential = async (token, sessionToken) => {
  if (!sessionToken) return;
  const db = await openCaptureDb(token);
  await db.put('meta', { key: 'sessionCredential', value: sessionToken });
};

/** See setSessionCredential. Returns null if none was ever persisted. */
const getSessionCredential = async (token) => {
  const db = await openCaptureDb(token);
  const row = await db.get('meta', 'sessionCredential');
  return row?.value ?? null;
};

const deleteDatabase = async (token) => {
  // Close our own cached connection first — IndexedDB's deleteDatabase()
  // blocks indefinitely while any connection to the database stays open.
  const existing = dbPromises.get(token);
  dbPromises.delete(token);
  if (existing) {
    try {
      const db = await existing;
      db.close();
    } catch {
      // Never opened successfully — nothing to close.
    }
  }
  await deleteDB(dbNameForToken(token));
};

/**
 * Best-effort backstop against abandoned-session storage leaks. Not
 * relied upon for correctness — just keeps device storage bounded over
 * time since this product doesn't track a precise per-token lifecycle
 * for every possible exit path.
 */
const sweepStaleDatabases = async () => {
  try {
    if (typeof indexedDB.databases !== 'function') return; // not supported (e.g. Safari)
    const databases = await indexedDB.databases();
    const now = Date.now();
    await Promise.all(
      databases
        .filter((d) => d.name?.startsWith('capture-offline-'))
        .map(async (d) => {
          try {
            const db = await openDB(d.name, DB_VERSION);
            const mediaRows = await db.getAll('media');
            const mutationRows = await db.getAll('mutations');
            const timestamps = [...mediaRows.map((r) => r.createdAt), ...mutationRows.map((r) => r.createdAt)];
            const lastActivity = timestamps.length > 0 ? Math.max(...timestamps) : 0;
            db.close();
            if (now - lastActivity > STALE_DB_MAX_AGE_MS) {
              await deleteDB(d.name);
            }
          } catch {
            // Corrupt/unreadable DB — leave it, not worth failing boot over.
          }
        })
    );
  } catch {
    // Sweep is best-effort only.
  }
};

export default {
  openCaptureDb,
  saveMedia,
  getMedia,
  updateMediaStatus,
  getPendingMedia,
  getQueuedAssignmentItemIds,
  enqueueMutation,
  getPendingMutations,
  updateMutation,
  supersedeMutations,
  supersedeMutationsForItem,
  cancelQueuedUpload,
  upsertNoteLocal,
  getNotesLocal,
  getNoteLocal,
  removeNoteLocal,
  reconcileNotesFromServer,
  setSessionCredential,
  getSessionCredential,
  deleteDatabase,
  sweepStaleDatabases,
  buildPhotoTargetKey,
};
